import { useCallback, useEffect, useMemo, useState } from "react";
import { GPS, TrapSite } from "../../../types";
import { defaultRadius } from "../../../utils/geo";
import {
  ContextMenuState,
  ContextMenuTarget,
  DeviceDraft,
  EditorDevice,
  EditorMode,
  EditorTarget,
  StagedMoves,
  TrapsiteEditorApi,
  TrapsiteDraft,
} from "./types";

const NO_MOVES: StagedMoves = { devices: {}, trapSites: {} };

/** The radius a new location starts at, in the viewer's own units. */
const defaultRadiusDraft = () => {
  const { value, unit } = defaultRadius();
  return { radiusValue: value, radiusUnit: unit };
};

const blankDraft = (gps: GPS): TrapsiteDraft => ({
  displayName: "",
  description: "",
  address: "",
  details: null,
  gps,
  geocodedGps: null,
  ...defaultRadiusDraft(),
  deviceIds: [],
});

const draftFromSite = (
  site: Partial<TrapSite>,
  deviceIds: string[],
): TrapsiteDraft => ({
  displayName: site.display_name ?? "",
  description: site.description ?? "",
  address: site.address ?? "",
  details: site.details ?? null,
  gps: site.gps ?? { lat: 0, lon: 0 },
  // Not looked up yet: an existing address is taken on trust until the user
  // asks us to find it, so we do not spend a billable geocode on every open.
  geocodedGps: null,
  radiusValue: site.radius_value ?? defaultRadiusDraft().radiusValue,
  radiusUnit: site.radius_unit ?? defaultRadiusDraft().radiusUnit,
  deviceIds,
});

/**
 * All of the editor's state.
 *
 * Deliberately local rather than a redux slice: it is ephemeral and scoped to
 * one map instance, and MapComponent remounts per route, which gives exactly
 * the right lifetime for free. A slice would survive navigation, so a
 * half-filled create form would reappear on a page where the map means
 * something else.
 */
export const useTrapsiteEditor = (
  enabled: boolean,
  devicesForSite: (siteId: string) => string[],
  /**
   * Where to start a pin that has nowhere else to start from - the org's map
   * centre. Without it a trap with no recorded position opens at 0,0, which
   * is in the Atlantic and miles from anything the user can drag it to.
   */
  fallbackGps: GPS,
): TrapsiteEditorApi => {
  const [target, setTarget] = useState<EditorTarget | null>(null);
  const [mode, setMode] = useState<EditorMode | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TrapsiteDraft | null>(null);
  const [deviceDraft, setDeviceDraft] = useState<DeviceDraft | null>(null);
  const [staged, setStaged] = useState<StagedMoves>(NO_MOVES);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  const openMenu = useCallback(
    (point: { x: number; y: number }, target: ContextMenuTarget) => {
      if (!enabled) return;
      setMenu({ x: point.x, y: point.y, target });
    },
    [enabled],
  );

  const startCreate = useCallback((gps: GPS) => {
    setTarget("trapsite");
    setMode("create");
    setEditingId(null);
    setDraft(blankDraft(gps));
    setDeviceDraft(null);
    setStaged(NO_MOVES);
    setMenu(null);
  }, []);

  const startEdit = useCallback(
    (site: Partial<TrapSite>) => {
      // listTrapSites returns Partial<TrapSite>, so an id is not guaranteed at
      // the type level. Opening an edit panel with no target would strand the
      // user on a form whose Save cannot work.
      if (!site?._id) return;
      setTarget("trapsite");
      setMode("edit");
      setEditingId(site._id);
      setDraft(draftFromSite(site, devicesForSite(site._id)));
      setDeviceDraft(null);
      setStaged(NO_MOVES);
      setMenu(null);
    },
    [devicesForSite],
  );

  /**
   * Place a trap at a point before saying which trap it is.
   *
   * The device is deliberately left unchosen; the point the user clicked is
   * the thing being asserted, and picking a device must not overwrite it.
   */
  const startMoveTrap = useCallback(
    (gps: GPS, trapSiteId: string | null = null) => {
      setTarget("device");
      setMode("create");
      setEditingId(null);
      setDraft(null);
      setDeviceDraft({
        deviceUuid: null,
        displayName: "",
        // Pre-assigned when the map is already showing one location: placing a
        // trap on that location's map means placing it at that location.
        trapSiteId,
        gps,
      });
      setStaged(NO_MOVES);
      setMenu(null);
    },
    [],
  );

  const startEditTrap = useCallback(
    (device: EditorDevice) => {
      setTarget("device");
      setMode("edit");
      setEditingId(device.uuid);
      setDraft(null);
      setDeviceDraft({
        deviceUuid: device.uuid,
        displayName: device.displayName,
        trapSiteId: device.trapSiteId,
        gps: device.gps ?? fallbackGps,
      });
      setStaged(NO_MOVES);
      setMenu(null);
    },
    [fallbackGps],
  );

  const patchDeviceDraft = useCallback((patch: Partial<DeviceDraft>) => {
    setDeviceDraft((current) => (current ? { ...current, ...patch } : current));
  }, []);

  /**
   * Choose which trap is being placed.
   *
   * Switching from one trap to another adopts the new trap's own position -
   * the pin was describing the old trap and would otherwise silently claim to
   * describe the new one. Choosing the FIRST trap keeps the point already on
   * the map, because in the Move Trap flow that point is the whole intent.
   */
  const selectDevice = useCallback((device: EditorDevice | null) => {
    setDeviceDraft((current) => {
      if (!current) return current;
      if (!device) {
        return { ...current, deviceUuid: null, displayName: "" };
      }
      const hadDevice = current.deviceUuid !== null;
      return {
        ...current,
        deviceUuid: device.uuid,
        displayName: device.displayName,
        trapSiteId: device.trapSiteId,
        gps: hadDevice && device.gps ? device.gps : current.gps,
      };
    });
  }, []);

  const stageDeviceMove = useCallback((uuid: string, gps: GPS) => {
    setStaged((current) => ({
      ...current,
      devices: { ...current.devices, [uuid]: gps },
    }));
  }, []);

  const stageTrapSiteMove = useCallback((id: string, gps: GPS) => {
    setStaged((current) => ({
      ...current,
      trapSites: { ...current.trapSites, [id]: gps },
    }));
  }, []);

  const patchDraft = useCallback((patch: Partial<TrapsiteDraft>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }, []);

  // Whichever draft is open owns the primary pin.
  const movePinTo = useCallback((gps: GPS) => {
    setDraft((current) => (current ? { ...current, gps } : current));
    setDeviceDraft((current) => (current ? { ...current, gps } : current));
  }, []);

  const close = useCallback(() => {
    setTarget(null);
    setMode(null);
    setEditingId(null);
    setDraft(null);
    setDeviceDraft(null);
    setStaged(NO_MOVES);
    setMenu(null);
  }, []);

  // rc-dropdown only handles Escape while focus is inside the menu, and here
  // focus is still on the map, so the key has to be caught at the document.
  useEffect(() => {
    if (!menu) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menu, closeMenu]);

  const value = {
    enabled,
    target,
    mode,
    editingId,
    draft,
    deviceDraft,
    staged,
    menu,
    openMenu,
    closeMenu,
    startCreate,
    startEdit,
    patchDraft,
    startMoveTrap,
    startEditTrap,
    patchDeviceDraft,
    selectDevice,
    movePinTo,
    stageDeviceMove,
    stageTrapSiteMove,
    close,
  };

  // Every field is either state or a stable useCallback, so spreading into the
  // dependency list keeps the memo honest without listing twenty names twice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => value, Object.values(value));
};
